import { ErrorCode, PlaybackError, generatePsid, AssetType, assetTypeToTelemetryAssetType, MediaTrackRole, } from '@zattoo/playback-sdk';
import { watchParamsFromCapability, watchParamsFromPlayOptions, watchParamsFromStreamType, watchParamsRakuten, mediaItemFromWatchResponse, gt12ChannelInfo as getGt12ChannelInfo, } from '../utils';
import { adsInfoFromWatchResponse } from '../../ads/utils';
import { EventKey as ZapiTelemetryEventKey } from '../../telemetry/enums';
import { assetIdFromRequestInfo } from '../../telemetry/utils';
import { getZapiStream, getStreamingProtocol, isRakutenStreamRequest, isRakutenVodRequest, selectTelemetryAudioStreamingOption, } from './utils';
import { ZapiEventKey } from '../../player/modules/event/event-key';
import { getPlaybackCapability } from '../../capability';
export const PlayerWithStreamRequest = (Base) => {
    return class extends Base {
        async requestStream(streamRequestInfo) {
            try {
                await this.stop();
                const sessionAbortController = this.startNewSession();
                const psid = generatePsid();
                this.psid = psid;
                const playOptions = streamRequestInfo.playOptions;
                const isRakuten = isRakutenStreamRequest(streamRequestInfo);
                const streamProvider = isRakuten
                    ? streamRequestInfo.streamProvider
                    : undefined;
                const capability = await getPlaybackCapability(streamProvider);
                const streamingOptions = isRakutenVodRequest(streamRequestInfo)
                    ? streamRequestInfo.streamingOptions
                    : undefined;
                const audioStreamingOption = selectTelemetryAudioStreamingOption({
                    streamingOptions,
                    capability,
                    preferredAudioLanguage: playOptions.preferredAudioLanguage,
                    preferredAudioFormat: playOptions.preferredAudioFormat,
                });
                if (sessionAbortController.signal.aborted) {
                    throw new PlaybackError(ErrorCode.SessionAbort, 'Session start aborted by new Session');
                }
                const streamType = getStreamingProtocol(playOptions, capability, streamProvider);
                this.triggerTelemetryNewSessionEvent(psid, assetTypeToTelemetryAssetType(streamRequestInfo.assetType), assetIdFromRequestInfo(streamRequestInfo), streamType, audioStreamingOption?.token, streamProvider);
                const commonWatchParams = {
                    ...watchParamsFromStreamType(streamType),
                    ...watchParamsFromPlayOptions(playOptions, streamProvider),
                    ...watchParamsFromCapability(streamType, capability, streamProvider),
                };
                const getRakutenWatchParams = () => {
                    if (!isRakuten) {
                        return {};
                    }
                    return watchParamsRakuten(streamRequestInfo, capability, playOptions);
                };
                const streamWatchParams = {
                    ...streamRequestInfo,
                    watchRequestParams: {
                        ...streamRequestInfo.watchRequestParams,
                        ...commonWatchParams,
                        ...getRakutenWatchParams(),
                    },
                };
                this.updateStreamInformation(streamWatchParams, playOptions);
                // todo: dispatch telemetry based on sdk events
                // https://zattoo2.atlassian.net/browse/PLY-1702
                this.dispatchCustomTelemetryEvent({
                    psid,
                    type: ZapiTelemetryEventKey.WATCH_SENT,
                });
                this.triggerEvent({
                    type: ZapiEventKey.WATCH_REQUESTED,
                    psid,
                });
                this.zapiWatchResponse = await getZapiStream(streamWatchParams);
                if (sessionAbortController.signal.aborted) {
                    throw new PlaybackError(ErrorCode.SessionAbort, 'Session start aborted by new Session');
                }
                if (!this.zapiWatchResponse) {
                    throw new Error('No watch response');
                }
                const zapiMedia = mediaItemFromWatchResponse(streamRequestInfo.assetType, this.zapiWatchResponse, streamRequestInfo.assetType === AssetType.REPLAY
                    ? streamRequestInfo.watchRequestParams.dynamic_pvr
                    : false);
                // todo: dispatch telemetry based on sdk events
                // https://zattoo2.atlassian.net/browse/PLY-1702
                this.dispatchCustomTelemetryEvent({
                    psid,
                    type: ZapiTelemetryEventKey.WATCH_RECEIVED,
                    url: zapiMedia.url,
                    license_url: zapiMedia.licenseUrl,
                    csid: this.zapiWatchResponse.csid,
                });
                this.triggerEvent({
                    type: ZapiEventKey.WATCH_RECEIVED,
                    psid,
                    data: this.zapiWatchResponse,
                });
                const gt12ChannelInfo = getGt12ChannelInfo(playOptions, this.zapiWatchResponse);
                await this.playUrl({
                    url: zapiMedia.url,
                    licenseUrl: zapiMedia.licenseUrl,
                    streamType,
                    fairPlayCertificateUrl: playOptions.fairPlayCertificateUrl,
                }, {
                    ...playOptions,
                    isInternalCall: true,
                    adsInfo: adsInfoFromWatchResponse(this.zapiWatchResponse.stream.ads, this.playOptions?.gt12ChannelInfo?.disableStartAd),
                    startupPosition: playOptions.startupPosition === undefined
                        ? undefined
                        : playOptions.startupPosition + (zapiMedia.prePadding ?? 0),
                    gt12ChannelInfo,
                    assetInfo: {
                        assetType: streamRequestInfo.assetType,
                        csid: this.zapiWatchResponse.csid,
                        id: assetIdFromRequestInfo(streamRequestInfo),
                        tcString: playOptions.tcString,
                        adid: playOptions.adid,
                        telemetryEndpoint: this.zapiWatchResponse.tracking?.temp_telemetry_url,
                    },
                    ...(this.zapiWatchResponse.custom_data ? {
                        customPlayreadyHeaders: {
                            'http-header-customData': this.zapiWatchResponse.custom_data,
                        },
                    } : {}),
                    externalSubtitles: [
                        ...playOptions.externalSubtitles ?? [],
                        ...this.zapiWatchResponse.all_subtitles?.map((subtitle) => {
                            const role = subtitle.forced
                                ? MediaTrackRole.FORCED
                                : MediaTrackRole.CAPTION;
                            const locale = subtitle.locale;
                            const codec = subtitle.format;
                            return {
                                roles: [role],
                                url: subtitle.url,
                                locale,
                                codec,
                                id: `${locale}-${role}-${codec}`,
                            };
                        }) ?? [],
                    ],
                });
                this.zapiDispatchMedia(zapiMedia, this.zapiWatchResponse);
                return zapiMedia;
            }
            catch (error) {
                this.handleError(error);
                return Promise.reject(error);
            }
        }
    };
};
